/**
 * MarkdownRenderer — Rich markdown rendering for AI chat messages
 *
 * Uses react-markdown with remark-gfm for GitHub Flavored Markdown.
 * Production-grade typography with:
 *  - Variable font sizes based on importance hierarchy
 *  - Accent colors for key elements (headings, bold, links)
 *  - Status tag icons ([done], [warning], etc.) replacing emojis
 *  - Dark/light mode aware color system
 *  - ChatGPT-style structured output rendering
 */

import React, { useState, useCallback, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useNavigate } from 'react-router-dom';
import { cn } from '../../utils/cn';

/* ── Status Tag Icons — replaces emojis with inline SVG icons ───────────── */

const STATUS_ICONS = {
  done: (
    <svg className="inline-block w-4 h-4 text-emerald-500 dark:text-emerald-400 mr-1 -mt-0.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
    </svg>
  ),
  warning: (
    <svg className="inline-block w-4 h-4 text-amber-500 dark:text-amber-400 mr-1 -mt-0.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
    </svg>
  ),
  error: (
    <svg className="inline-block w-4 h-4 text-red-500 dark:text-red-400 mr-1 -mt-0.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="m9.75 9.75 4.5 4.5m0-4.5-4.5 4.5M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
    </svg>
  ),
  info: (
    <svg className="inline-block w-4 h-4 text-blue-500 dark:text-blue-400 mr-1 -mt-0.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z" />
    </svg>
  ),
  progress: (
    <svg className="inline-block w-4 h-4 text-blue-500 dark:text-blue-400 mr-1 -mt-0.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182" />
    </svg>
  ),
  pending: (
    <svg className="inline-block w-4 h-4 text-gray-400 dark:text-gray-400 mr-1 -mt-0.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
    </svg>
  ),
  star: (
    <svg className="inline-block w-4 h-4 text-yellow-500 dark:text-yellow-400 mr-1 -mt-0.5" fill="currentColor" viewBox="0 0 24 24">
      <path fillRule="evenodd" d="M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.006 5.404.434c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.434 2.082-5.005Z" clipRule="evenodd" />
    </svg>
  ),
  target: (
    <svg className="inline-block w-4 h-4 text-violet-500 dark:text-violet-400 mr-1 -mt-0.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="5" />
      <circle cx="12" cy="12" r="1" fill="currentColor" />
    </svg>
  ),
};

// Regex to strip common emojis from AI text.
// Matches full ZWJ sequences (e.g. 👨‍👩‍👧‍👦), skin-tone modified emoji, flag sequences,
// and standalone emoji codepoints. The `u` flag enables Unicode property escapes.
const EMOJI_REGEX = /(?:\p{Emoji_Presentation}|\p{Emoji}\uFE0F)(?:\u200D(?:\p{Emoji_Presentation}|\p{Emoji}\uFE0F))*/gu;

// Status tag pattern: [done], [warning], etc.
const STATUS_TAG_REGEX = /\[(done|warning|error|info|progress|pending|star|target)\]/g;

/**
 * Pre-process AI content:
 * 1. Strip emoji characters (but preserve emoji inside code blocks)
 * 2. Replace [tag] markers with placeholder for rendering
 */
function preprocessContent(content) {
  if (!content) return content;

  // Preserve code blocks from emoji stripping
  const codeBlocks = [];
  let processed = content.replace(/```[\s\S]*?```|`[^`\n]+`/g, (match) => {
    codeBlocks.push(match);
    return `\x00CB${codeBlocks.length - 1}\x00`;
  });

  // Strip emojis outside code blocks
  processed = processed.replace(EMOJI_REGEX, '');
  // Clean up any double-spaces left by emoji removal
  processed = processed.replace(/  +/g, ' ').replace(/^ +/gm, (match) => match);

  // Restore code blocks
  processed = processed.replace(/\x00CB(\d+)\x00/g, (_, idx) => codeBlocks[idx]);

  return processed;
}

/* ── Copy button for code blocks ────────────────────────────────────────── */

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [text]);

  return (
    <button
      onClick={handleCopy}
      className="absolute top-2 right-2 px-2 py-1 rounded-md text-[11px] font-medium
        bg-gray-700/80 hover:bg-gray-600 text-gray-300 hover:text-white backdrop-blur-sm
        transition-all duration-150 opacity-0 group-hover:opacity-100 shadow-sm"
      title="Copy code"
    >
      {copied ? (
        <span className="flex items-center gap-1">
          <svg className="w-3 h-3 text-emerald-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
          Copied!
        </span>
      ) : (
        <span className="flex items-center gap-1">
          <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
            <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
          </svg>
          Copy
        </span>
      )}
    </button>
  );
}

/* ── Custom markdown components factory ──────────────────────────────────── */

/**
 * Build the react-markdown custom components map.
 * Accepts `navigate` so internal links can use React Router navigation
 * instead of opening a new tab.
 */
function buildComponents(navigate) {
  return {
  // Code blocks
  code({ node, inline, className, children, ...props }) {
    const match = /language-(\w+)/.exec(className || '');
    const lang = match?.[1];
    const text = String(children).replace(/\n$/, '');

    if (!inline && (lang || text.includes('\n'))) {
      return (
        <div className="group relative my-4 rounded-xl overflow-hidden border border-gray-200/80 dark:border-gray-700/50 shadow-sm">
          {lang && (
            <div className="flex items-center justify-between px-4 py-2 bg-gradient-to-r from-gray-100 to-gray-50 dark:from-gray-800 dark:to-gray-850 border-b border-gray-200/80 dark:border-gray-700/50">
              <span className="text-[10px] font-bold text-gray-500 dark:text-gray-300 uppercase tracking-[0.15em]">
                {lang}
              </span>
            </div>
          )}
          <div className="relative">
            <pre className="overflow-x-auto p-4 bg-[#fafafa] dark:bg-[#0d1117] text-[12.5px] leading-[1.65]">
              <code className="text-gray-800 dark:text-gray-200 font-mono" {...props}>
                {text}
              </code>
            </pre>
            <CopyButton text={text} />
          </div>
        </div>
      );
    }

    // Inline code — handle status tags like [done], [warning] etc.
    const codeText = String(children).trim();
    const statusIcon = STATUS_ICONS[codeText];
    if (statusIcon) {
      return statusIcon;
    }

    return (
      <code
        className="px-1.5 py-0.5 rounded-md text-[12px] font-mono font-medium
          bg-violet-50 dark:bg-violet-950/30 text-violet-600 dark:text-violet-400
          border border-violet-100/80 dark:border-violet-800/40"
        {...props}
      >
        {children}
      </code>
    );
  },

  // Paragraphs — render status tags as inline icons
  p({ children }) {
    return (
      <p className="text-[13.5px] leading-[1.75] mb-3 last:mb-0 text-gray-800 dark:text-gray-200">
        {renderChildrenWithStatusIcons(children)}
      </p>
    );
  },

  // Headers — expressive hierarchy with distinct sizes and accent colors
  h1({ children }) {
    return (
      <h1 className="text-[20px] font-extrabold mb-3.5 mt-6 first:mt-0 text-gray-900 dark:text-white tracking-tight leading-snug">
        {children}
      </h1>
    );
  },
  h2({ children }) {
    return (
      <h2 className="text-[17px] font-bold mb-3 mt-5 first:mt-0 text-gray-800 dark:text-gray-100 tracking-tight leading-snug">
        {children}
      </h2>
    );
  },
  h3({ children }) {
    return (
      <h3 className="text-[15px] font-semibold mb-2.5 mt-4 first:mt-0 text-indigo-700 dark:text-indigo-300 leading-snug">
        {children}
      </h3>
    );
  },
  h4({ children }) {
    return (
      <h4 className="text-[13.5px] font-semibold mb-2 mt-3.5 first:mt-0 text-gray-700 dark:text-gray-300 uppercase tracking-wide">
        {children}
      </h4>
    );
  },
  h5({ children }) {
    return (
      <h5 className="text-[13px] font-semibold mb-1.5 mt-3 first:mt-0 text-gray-600 dark:text-gray-400">
        {children}
      </h5>
    );
  },
  h6({ children }) {
    return (
      <h6 className="text-[12.5px] font-medium mb-1.5 mt-2.5 first:mt-0 text-gray-500 dark:text-gray-400 uppercase tracking-wider">
        {children}
      </h6>
    );
  },

  // Lists — colored bullets with better spacing
  ul({ children, className }) {
    const isTaskList = className?.includes('contains-task-list');
    return (
      <ul className={cn(
        'mb-3 text-[13.5px]',
        isTaskList
          ? 'list-none pl-0.5 space-y-2'
          : 'list-none pl-0 space-y-1.5 [&>li]:relative [&>li]:pl-5 [&>li]:before:content-[""] [&>li]:before:absolute [&>li]:before:left-0.5 [&>li]:before:top-[10px] [&>li]:before:w-[6px] [&>li]:before:h-[6px] [&>li]:before:rounded-full [&>li]:before:bg-indigo-400 dark:[&>li]:before:bg-indigo-500'
      )}>
        {children}
      </ul>
    );
  },
  ol({ children }) {
    return (
      <ol className="list-decimal list-outside pl-5 mb-3 space-y-1.5 text-[13.5px]
        marker:text-indigo-500 dark:marker:text-indigo-400 marker:font-bold"
      >
        {children}
      </ol>
    );
  },
  li({ children, className, ...props }) {
    const isTask = className?.includes('task-list-item');
    if (isTask) {
      return (
        <li className="flex items-start gap-2 leading-relaxed list-none py-1
          rounded-lg px-2.5 -mx-2 hover:bg-gray-50 dark:hover:bg-gray-800/30 transition-colors" {...props}>
          {children}
        </li>
      );
    }
    return (
      <li className="leading-[1.7] text-gray-800 dark:text-gray-200" {...props}>
        {renderChildrenWithStatusIcons(children)}
      </li>
    );
  },

  // Checkbox (GFM task list)
  input({ type, checked, disabled }) {
    if (type === 'checkbox') {
      return (
        <span className={cn(
          'inline-flex items-center justify-center w-4 h-4 rounded-[5px] border-[1.5px] mt-0.5 flex-shrink-0 transition-colors',
          checked
            ? 'bg-emerald-500 border-emerald-500 dark:bg-emerald-600 dark:border-emerald-600 shadow-sm shadow-emerald-500/20'
            : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800'
        )}>
          {checked && (
            <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          )}
        </span>
      );
    }
    return null;
  },

  // Blockquote — with info icon and tinted background
  blockquote({ children }) {
    return (
      <blockquote className="relative flex gap-3 border-l-[3px] border-l-blue-400 dark:border-l-blue-500
        pl-3.5 pr-3 py-2.5 my-3 rounded-r-lg
        bg-blue-50/60 dark:bg-blue-950/20
        text-[13px] text-gray-700 dark:text-gray-300
        [&>p]:mb-1 [&>p:last-child]:mb-0 [&>p]:not-italic">
        <div className="flex-1">{children}</div>
      </blockquote>
    );
  },

  // Links
  a({ href, children }) {
    const isInternal = href && (href.startsWith('/') || href.startsWith('#'));

    if (isInternal && navigate) {
      // Detect entity detail links (tasks, projects, departments, teams)
      const isEntityLink = /^\/(org\/)?(tasks|projects|departments|teams)\/[a-f\d]{24}$/i.test(href);

      return (
        <a
          href={href}
          onClick={(e) => {
            e.preventDefault();
            navigate(href);
          }}
          className={cn(
            'font-medium cursor-pointer transition-colors duration-150',
            isEntityLink
              ? 'text-teal-600 dark:text-teal-400 hover:text-teal-800 dark:hover:text-teal-200 underline decoration-teal-400/50 dark:decoration-teal-500/50 underline-offset-2 hover:decoration-teal-600 dark:hover:decoration-teal-300'
              : 'text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 underline decoration-teal-300/40 dark:decoration-teal-600/40 underline-offset-2 hover:decoration-teal-500 dark:hover:decoration-teal-400'
          )}
        >
          {children}
        </a>
      );
    }

    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-0.5 text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300
          underline decoration-teal-300/40 dark:decoration-teal-600/40 underline-offset-2
          hover:decoration-teal-500 dark:hover:decoration-teal-400
          font-medium transition-colors duration-150"
      >
        {children}
        <svg className="w-3 h-3 flex-shrink-0 opacity-40" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
        </svg>
      </a>
    );
  },

  // Tables
  table({ children }) {
    return (
      <div className="overflow-x-auto my-4 rounded-xl border border-gray-200/80 dark:border-gray-700/50 shadow-sm">
        <table className="min-w-full text-[12.5px]">{children}</table>
      </div>
    );
  },
  thead({ children }) {
    return <thead className="bg-gradient-to-r from-gray-50 to-gray-100/80 dark:from-gray-800/80 dark:to-gray-800/60">{children}</thead>;
  },
  tbody({ children }) {
    return <tbody className="[&>tr:nth-child(even)]:bg-gray-50/40 dark:[&>tr:nth-child(even)]:bg-gray-800/20">{children}</tbody>;
  },
  tr({ children }) {
    return <tr className="transition-colors hover:bg-indigo-50/30 dark:hover:bg-indigo-950/10">{children}</tr>;
  },
  th({ children }) {
    return (
      <th className="px-3 py-2.5 text-left text-[10.5px] font-bold text-gray-500 dark:text-gray-300 uppercase tracking-[0.1em] border-b border-gray-200 dark:border-gray-700">
        {children}
      </th>
    );
  },
  td({ children }) {
    return (
      <td className="px-3 py-2 text-[12.5px] text-gray-800 dark:text-gray-200 border-b border-gray-100 dark:border-gray-700/30">
        {children}
      </td>
    );
  },

  // Horizontal rule — clean gradient divider
  hr() {
    return (
      <div className="my-5 flex items-center">
        <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-300 dark:via-gray-700 to-transparent" />
      </div>
    );
  },

  // Strong — accent-colored bold text (key visual differentiator)
  strong({ children }) {
    return <strong className="font-semibold text-gray-900 dark:text-gray-50">{children}</strong>;
  },
  // Emphasis — softer, smaller for secondary info
  em({ children }) {
    return <em className="text-[12.5px] not-italic text-gray-600 dark:text-gray-300">{children}</em>;
  },

  // Del (strikethrough)
  del({ children }) {
    return <del className="line-through text-gray-400 dark:text-gray-500 text-[12.5px]">{children}</del>;
  },
  };
}

/**
 * Process React children to replace [status_tag] text with inline icon components.
 * Handles both plain text children and mixed React element children.
 */
function renderChildrenWithStatusIcons(children) {
  if (!children) return children;

  const processNode = (child) => {
    if (typeof child === 'string') {
      // Check if the string contains any status tags
      if (!STATUS_TAG_REGEX.test(child)) return child;
      STATUS_TAG_REGEX.lastIndex = 0; // Reset regex state

      const parts = [];
      let lastIdx = 0;
      let match;

      while ((match = STATUS_TAG_REGEX.exec(child)) !== null) {
        // Add text before the match
        if (match.index > lastIdx) {
          parts.push(child.slice(lastIdx, match.index));
        }
        // Add the icon
        const tag = match[1];
        if (STATUS_ICONS[tag]) {
          parts.push(<span key={`icon-${match.index}`}>{STATUS_ICONS[tag]}</span>);
        }
        lastIdx = match.index + match[0].length;
      }
      // Add remaining text
      if (lastIdx < child.length) {
        parts.push(child.slice(lastIdx));
      }
      return parts.length === 1 ? parts[0] : parts;
    }
    return child;
  };

  if (Array.isArray(children)) {
    return children.flatMap((child, i) => {
      const result = processNode(child);
      return Array.isArray(result) ? result : [result];
    });
  }

  const result = processNode(children);
  return Array.isArray(result) ? result : children;
}

/* ── Main Component ─────────────────────────────────────────────────────── */

export default function MarkdownRenderer({ content, className }) {
  const navigate = useNavigate();
  const components = useMemo(() => buildComponents(navigate), [navigate]);

  if (!content) return null;

  // Pre-process: strip emojis, clean whitespace
  const processed = preprocessContent(content);

  return (
    <div className={cn(
      'markdown-body',
      '[&>*:first-child]:mt-0 [&>*:last-child]:mb-0',
      className
    )}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {processed}
      </ReactMarkdown>
    </div>
  );
}
